/**
 * Triveny Roles & Permissions
 *
 * Admin surface for the platform RBAC system. Mirrors the WeaveIn
 * UnifiedAccessControl structure but is fed by /api/platform/roles —
 * keeping platform_shared role definitions completely isolated from the
 * healthcare role_terminology / user_organization_roles tables.
 *
 *   GET    /api/platform/roles                 → list role definitions
 *   GET    /api/platform/roles/capabilities    → curated capability registry
 *   POST   /api/platform/roles                 → create custom role
 *   PUT    /api/platform/roles/{id}            → update role
 *   DELETE /api/platform/roles/{id}            → delete custom role
 *
 * tenant_admin / platform_admin can create/edit/delete. Everyone in the
 * tenant can read so the UI can render the current role landscape.
 */

import { useTabbedChrome } from '@/components/weavein';
import { useEffect, useMemo, useState } from 'react';
import { config } from '@/src/config';
import { tvTokens } from '@/src/platform/shared/components/triveny';
import { authFetch } from '@/lib/authFetch';
import { usePlatformCapability } from '@/hooks/usePlatformCapability';
import { Shield, Plus, Trash2, Save, Lock, Pencil, X, AlertCircle, ShieldOff } from 'lucide-react';

interface RoleDef {
  id: string;
  role_key: string;
  display_name: string;
  description?: string | null;
  access_level: number;
  is_system: boolean;
  is_active: boolean;
  capabilities: string[];
}

interface CapDef {
  key: string;
  label: string;
  group: string;
}

const SECTION_ORDER = ['Design', 'Market', 'Engage', 'Operate', 'Admin'];

export default function PlatformRolesPage() {
  const tabbed = useTabbedChrome();
  const { can, isLoading: capsLoading, primaryRoleKey } = usePlatformCapability();
  const canManageRoles = can('role_management');

  const [roles, setRoles] = useState<RoleDef[]>([]);
  const [caps, setCaps] = useState<CapDef[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [draft, setDraft] = useState<{
    display_name: string;
    description: string;
    capabilities: Set<string>;
  } | null>(null);
  const [creating, setCreating] = useState(false);
  const [saving, setSaving] = useState(false);

  const baseUrl = config.weaveinApi.baseUrl;

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const [rolesRes, capsRes] = await Promise.all([
        authFetch(`${baseUrl}/api/platform/roles`),
        authFetch(`${baseUrl}/api/platform/roles/capabilities`),
      ]);
      if (!rolesRes.ok) throw new Error(`Roles: ${rolesRes.status}`);
      if (!capsRes.ok) throw new Error(`Capabilities: ${capsRes.status}`);
      const rolesData = (await rolesRes.json()) as RoleDef[];
      const capsData = (await capsRes.json()) as { capabilities: CapDef[] };
      setRoles(rolesData);
      setCaps(capsData.capabilities);
    } catch (err: any) {
      setError(err?.message || 'Failed to load roles');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const capsByGroup = useMemo(() => {
    const grouped: Record<string, CapDef[]> = {};
    for (const c of caps) {
      grouped[c.group] = grouped[c.group] || [];
      grouped[c.group].push(c);
    }
    return grouped;
  }, [caps]);

  const startEdit = (role: RoleDef) => {
    setEditingId(role.id);
    setDraft({
      display_name: role.display_name,
      description: role.description || '',
      capabilities: new Set(role.capabilities),
    });
  };
  const startCreate = () => {
    setCreating(true);
    setEditingId(null);
    setDraft({ display_name: '', description: '', capabilities: new Set() });
  };
  const cancelEdit = () => {
    setEditingId(null);
    setCreating(false);
    setDraft(null);
  };

  const toggleCap = (key: string) => {
    if (!draft) return;
    const next = new Set(draft.capabilities);
    if (next.has(key)) next.delete(key);
    else next.add(key);
    setDraft({ ...draft, capabilities: next });
  };

  const save = async () => {
    if (!draft) return;
    if (!draft.display_name.trim()) {
      setError('Display name is required');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const body = {
        display_name: draft.display_name.trim(),
        description: draft.description.trim() || null,
        capabilities: Array.from(draft.capabilities),
      };
      const url = creating
        ? `${baseUrl}/api/platform/roles`
        : `${baseUrl}/api/platform/roles/${editingId}`;
      const res = await authFetch(url, {
        method: creating ? 'POST' : 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data?.detail || `Save failed (${res.status})`);
      }
      cancelEdit();
      await load();
    } catch (err: any) {
      setError(err?.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const remove = async (role: RoleDef) => {
    if (role.is_system) return;
    if (!confirm(`Delete role "${role.display_name}"? Users with only this role will lose access.`)) return;
    try {
      const res = await authFetch(`${baseUrl}/api/platform/roles/${role.id}`, { method: 'DELETE' });
      if (!res.ok && res.status !== 204) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data?.detail || `Delete failed (${res.status})`);
      }
      await load();
    } catch (err: any) {
      setError(err?.message || 'Delete failed');
    }
  };

  // Gate the page on the `role_management` capability. Non-admins
  // (tenant_user / tenant_viewer) see a clear no-access state instead
  // of an empty editable surface they can't actually save.
  if (!capsLoading && !canManageRoles) {
    return (
      <div
        className={`p-4 md:p-6 lg:p-8 max-w-2xl mx-auto${config.isTriveny ? ' triveny-platform' : ''}`}
      >
        <div
          style={{
            background: '#fff',
            border: `1px solid ${tvTokens.border}`,
            borderRadius: 12,
            padding: 32,
            textAlign: 'center',
          }}
        >
          <ShieldOff size={36} style={{ color: tvTokens.ink500, margin: '0 auto 12px' }} />
          <h1 style={{ margin: 0, fontSize: 18, fontWeight: 600, color: tvTokens.ink }}>
            Role management is restricted
          </h1>
          <p style={{ marginTop: 8, fontSize: 13, color: tvTokens.ink700 }}>
            Your current role <strong>{primaryRoleKey || 'tenant_user'}</strong> doesn't include
            the <code>role_management</code> capability. Ask a tenant admin if you need access.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div
      className={`p-4 md:p-6 lg:p-8 max-w-7xl mx-auto${config.isTriveny ? ' triveny-platform' : ''}`}
    >
      <header style={{ marginBottom: 24, display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          {!tabbed && <Shield size={28} style={{ color: tvTokens.ink }} />}
          <div>
            {!tabbed && (
            <h1
              style={{
                margin: 0,
                fontSize: 22,
                fontWeight: 600,
                color: tvTokens.ink,
                letterSpacing: '-0.01em',
              }}
            >
              Roles & Permissions
            </h1>
            )}
            <p style={{ margin: '4px 0 0', fontSize: 13, color: tvTokens.ink500 }}>
              Define what each role can do across Triveny. System roles can be renamed and rescoped;
              custom roles can be added for your tenant.
            </p>
          </div>
        </div>
        <div style={{ marginLeft: 'auto' }}>
          <button
            onClick={startCreate}
            disabled={creating || !!editingId}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              padding: '8px 14px',
              background: tvTokens.ink,
              color: '#fff',
              border: 'none',
              borderRadius: 8,
              fontSize: 13,
              fontWeight: 500,
              cursor: creating || editingId ? 'not-allowed' : 'pointer',
              opacity: creating || editingId ? 0.5 : 1,
            }}
          >
            <Plus size={14} /> New role
          </button>
        </div>
      </header>

      {error && (
        <div
          style={{
            marginBottom: 16,
            padding: '10px 12px',
            background: '#fef2f2',
            border: '1px solid #fecaca',
            borderRadius: 8,
            color: '#991b1b',
            fontSize: 13,
            display: 'flex',
            gap: 8,
            alignItems: 'flex-start',
          }}
        >
          <AlertCircle size={16} style={{ flexShrink: 0, marginTop: 1 }} />
          <span>{error}</span>
        </div>
      )}

      {loading ? (
        <div style={{ padding: 40, textAlign: 'center', color: tvTokens.ink500 }}>Loading roles…</div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: 16 }}>
          {creating && draft && (
            <RoleCard
              draft={draft}
              capsByGroup={capsByGroup}
              setDraft={setDraft}
              toggleCap={toggleCap}
              onSave={save}
              onCancel={cancelEdit}
              saving={saving}
              mode="create"
            />
          )}
          {roles.map((role) =>
            editingId === role.id && draft ? (
              <RoleCard
                key={role.id}
                role={role}
                draft={draft}
                capsByGroup={capsByGroup}
                setDraft={setDraft}
                toggleCap={toggleCap}
                onSave={save}
                onCancel={cancelEdit}
                saving={saving}
                mode="edit"
              />
            ) : (
              <RoleViewCard
                key={role.id}
                role={role}
                caps={caps}
                onEdit={() => startEdit(role)}
                onDelete={() => remove(role)}
                disabled={!!editingId || creating}
              />
            ),
          )}
        </div>
      )}
    </div>
  );
}

function RoleViewCard({
  role,
  caps,
  onEdit,
  onDelete,
  disabled,
}: {
  role: RoleDef;
  caps: CapDef[];
  onEdit: () => void;
  onDelete: () => void;
  disabled: boolean;
}) {
  const capLabels = useMemo(() => {
    const byKey = new Map(caps.map((c) => [c.key, c]));
    return role.capabilities
      .map((k) => byKey.get(k))
      .filter((c): c is CapDef => !!c);
  }, [role.capabilities, caps]);

  return (
    <div
      style={{
        background: '#fff',
        border: `1px solid ${tvTokens.border}`,
        borderRadius: 12,
        padding: 20,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 16, flexWrap: 'wrap' }}>
        <div style={{ flex: 1, minWidth: 240 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <h2
              style={{
                margin: 0,
                fontSize: 16,
                fontWeight: 600,
                color: tvTokens.ink,
              }}
            >
              {role.display_name}
            </h2>
            {role.is_system && (
              <span
                title="Built-in system role"
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 4,
                  padding: '2px 8px',
                  borderRadius: 999,
                  fontSize: 10,
                  fontWeight: 600,
                  textTransform: 'uppercase',
                  letterSpacing: '0.06em',
                  background: '#eef2ff',
                  color: '#3730a3',
                }}
              >
                <Lock size={10} /> System
              </span>
            )}
            <span
              style={{
                fontFamily: tvTokens.fontMono,
                fontSize: 11,
                color: tvTokens.ink500,
              }}
            >
              {role.role_key}
            </span>
          </div>
          {role.description && (
            <p style={{ margin: '6px 0 0', color: tvTokens.ink700, fontSize: 13 }}>
              {role.description}
            </p>
          )}
        </div>
        <div style={{ display: 'flex', gap: 6 }}>
          <button
            onClick={onEdit}
            disabled={disabled}
            title="Edit role"
            style={{
              padding: '6px 10px',
              background: 'transparent',
              color: tvTokens.ink,
              border: `1px solid ${tvTokens.border}`,
              borderRadius: 8,
              fontSize: 12,
              cursor: disabled ? 'not-allowed' : 'pointer',
              opacity: disabled ? 0.5 : 1,
              display: 'inline-flex',
              alignItems: 'center',
              gap: 4,
            }}
          >
            <Pencil size={12} /> Edit
          </button>
          {!role.is_system && (
            <button
              onClick={onDelete}
              disabled={disabled}
              title="Delete role"
              style={{
                padding: '6px 10px',
                background: 'transparent',
                color: '#b91c1c',
                border: `1px solid ${tvTokens.border}`,
                borderRadius: 8,
                fontSize: 12,
                cursor: disabled ? 'not-allowed' : 'pointer',
                opacity: disabled ? 0.5 : 1,
                display: 'inline-flex',
                alignItems: 'center',
                gap: 4,
              }}
            >
              <Trash2 size={12} />
            </button>
          )}
        </div>
      </div>

      <div style={{ marginTop: 12, display: 'flex', flexWrap: 'wrap', gap: 6 }}>
        {capLabels.length === 0 ? (
          <span style={{ fontSize: 12, color: tvTokens.ink500, fontStyle: 'italic' }}>
            No capabilities granted
          </span>
        ) : (
          capLabels.map((c) => (
            <span
              key={c.key}
              style={{
                padding: '3px 8px',
                background: '#f3f4f6',
                color: tvTokens.ink700,
                fontSize: 11,
                borderRadius: 999,
              }}
            >
              {c.label}
            </span>
          ))
        )}
      </div>
    </div>
  );
}

function RoleCard({
  role,
  draft,
  capsByGroup,
  setDraft,
  toggleCap,
  onSave,
  onCancel,
  saving,
  mode,
}: {
  role?: RoleDef;
  draft: { display_name: string; description: string; capabilities: Set<string> };
  capsByGroup: Record<string, CapDef[]>;
  setDraft: React.Dispatch<
    React.SetStateAction<
      { display_name: string; description: string; capabilities: Set<string> } | null
    >
  >;
  toggleCap: (key: string) => void;
  onSave: () => void;
  onCancel: () => void;
  saving: boolean;
  mode: 'edit' | 'create';
}) {
  return (
    <div
      style={{
        background: '#fff',
        border: `2px solid ${tvTokens.ink}`,
        borderRadius: 12,
        padding: 20,
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
        <h2 style={{ margin: 0, fontSize: 16, fontWeight: 600, color: tvTokens.ink }}>
          {mode === 'create' ? 'New role' : `Edit ${role?.display_name}`}
        </h2>
        <button
          onClick={onCancel}
          title="Cancel"
          style={{
            background: 'transparent',
            border: 'none',
            cursor: 'pointer',
            color: tvTokens.ink500,
            padding: 4,
          }}
        >
          <X size={16} />
        </button>
      </div>

      <label style={{ display: 'block', marginBottom: 12 }}>
        <span style={{ display: 'block', fontSize: 12, fontWeight: 500, color: tvTokens.ink, marginBottom: 4 }}>
          Display name
        </span>
        <input
          type="text"
          value={draft.display_name}
          onChange={(e) => setDraft({ ...draft, display_name: e.target.value })}
          disabled={saving}
          style={{
            width: '100%',
            padding: '8px 10px',
            border: `1px solid ${tvTokens.border}`,
            borderRadius: 8,
            fontSize: 13,
          }}
        />
      </label>

      <label style={{ display: 'block', marginBottom: 16 }}>
        <span style={{ display: 'block', fontSize: 12, fontWeight: 500, color: tvTokens.ink, marginBottom: 4 }}>
          Description
        </span>
        <textarea
          value={draft.description}
          onChange={(e) => setDraft({ ...draft, description: e.target.value })}
          disabled={saving}
          rows={2}
          style={{
            width: '100%',
            padding: '8px 10px',
            border: `1px solid ${tvTokens.border}`,
            borderRadius: 8,
            fontSize: 13,
            fontFamily: 'inherit',
            resize: 'vertical',
          }}
        />
      </label>

      <div
        style={{
          fontSize: 12,
          fontWeight: 600,
          color: tvTokens.ink,
          marginBottom: 8,
          textTransform: 'uppercase',
          letterSpacing: '0.06em',
        }}
      >
        Capabilities
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        {SECTION_ORDER.filter((g) => capsByGroup[g]?.length).map((group) => (
          <div key={group}>
            <div
              style={{
                fontSize: 10,
                fontWeight: 700,
                color: tvTokens.ink500,
                textTransform: 'uppercase',
                letterSpacing: '0.1em',
                marginBottom: 6,
              }}
            >
              {group}
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 4 }}>
              {capsByGroup[group].map((cap) => (
                <label
                  key={cap.key}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                    padding: '6px 8px',
                    borderRadius: 6,
                    cursor: 'pointer',
                    fontSize: 13,
                    color: tvTokens.ink700,
                    background: draft.capabilities.has(cap.key) ? '#f3f4f6' : 'transparent',
                  }}
                >
                  <input
                    type="checkbox"
                    checked={draft.capabilities.has(cap.key)}
                    onChange={() => toggleCap(cap.key)}
                    disabled={saving}
                  />
                  <span>{cap.label}</span>
                </label>
              ))}
            </div>
          </div>
        ))}
      </div>

      <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 18 }}>
        <button
          onClick={onCancel}
          disabled={saving}
          style={{
            padding: '8px 14px',
            background: 'transparent',
            color: tvTokens.ink,
            border: `1px solid ${tvTokens.border}`,
            borderRadius: 8,
            fontSize: 13,
            cursor: saving ? 'not-allowed' : 'pointer',
          }}
        >
          Cancel
        </button>
        <button
          onClick={onSave}
          disabled={saving || !draft.display_name.trim()}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
            padding: '8px 14px',
            background: tvTokens.ink,
            color: '#fff',
            border: 'none',
            borderRadius: 8,
            fontSize: 13,
            fontWeight: 500,
            cursor: saving ? 'not-allowed' : 'pointer',
            opacity: saving ? 0.6 : 1,
          }}
        >
          <Save size={14} />
          {saving ? 'Saving…' : 'Save'}
        </button>
      </div>
    </div>
  );
}
